export class StateCode {
    readonly code: string;
    readonly name: string;
    readonly fips: number;
    readonly isTerritory: boolean;

    private constructor(code: string, name: string, fips: number, isTerritory: boolean = false) {
        this.code = code;
        this.name = name;
        this.fips = fips;
        this.isTerritory = isTerritory;
    }

    public static readonly AK = new StateCode('AK', 'Alaska', 2);
    public static readonly AL = new StateCode('AL', 'Alabama', 1);
    public static readonly AR = new StateCode('AR', 'Arkansas', 5);
    public static readonly AZ = new StateCode('AZ', 'Arizona', 4);
    public static readonly CA = new StateCode('CA', 'California', 6);
    public static readonly CO = new StateCode('CO', 'Colorado', 8);
    public static readonly CT = new StateCode('CT', 'Connecticut', 9);
    public static readonly DC = new StateCode('DC', 'District of Columbia', 11);
    public static readonly DE = new StateCode('DE', 'Delaware', 10);
    public static readonly FL = new StateCode('FL', 'Florida', 12);
    public static readonly GA = new StateCode('GA', 'Georgia', 13);
    public static readonly HI = new StateCode('HI', 'Hawaii', 15);
    public static readonly IA = new StateCode('IA', 'Iowa', 19);
    public static readonly ID = new StateCode('ID', 'Idaho', 16);
    public static readonly IL = new StateCode('IL', 'Illinois', 17);
    public static readonly IN = new StateCode('IN', 'Indiana', 18);
    public static readonly KS = new StateCode('KS', 'Kansas', 20);
    public static readonly KY = new StateCode('KY', 'Kentucky', 21);
    public static readonly LA = new StateCode('LA', 'Louisiana', 22);
    public static readonly MA = new StateCode('MA', 'Massachusetts', 25);
    public static readonly MD = new StateCode('MD', 'Maryland', 24);
    public static readonly ME = new StateCode('ME', 'Maine', 23);
    public static readonly MI = new StateCode('MI', 'Michigan', 26);
    public static readonly MN = new StateCode('MN', 'Minnesota', 27);
    public static readonly MO = new StateCode('MO', 'Missouri', 29);
    public static readonly MS = new StateCode('MS', 'Mississippi', 28);
    public static readonly MT = new StateCode('MT', 'Montana', 30);
    public static readonly NC = new StateCode('NC', 'North Carolina', 37);
    public static readonly ND = new StateCode('ND', 'North Dakota', 38);
    public static readonly NE = new StateCode('NE', 'Nebraska', 31);
    public static readonly NH = new StateCode('NH', 'New Hampshire', 33);
    public static readonly NJ = new StateCode('NJ', 'New Jersey', 34);
    public static readonly NM = new StateCode('NM', 'New Mexico', 35);
    public static readonly NV = new StateCode('NV', 'Nevada', 32);
    public static readonly NY = new StateCode('NY', 'New York', 36);
    public static readonly OH = new StateCode('OH', 'Ohio', 39);
    public static readonly OK = new StateCode('OK', 'Oklahoma', 40);
    public static readonly OR = new StateCode('OR', 'Oregon', 41);
    public static readonly PA = new StateCode('PA', 'Pennsylvania', 42);
    public static readonly RI = new StateCode('RI', 'Rhode Island', 44);
    public static readonly SC = new StateCode('SC', 'South Carolina', 45);
    public static readonly SD = new StateCode('SD', 'South Dakota', 46);
    public static readonly TN = new StateCode('TN', 'Tennessee', 47);
    public static readonly TX = new StateCode('TX', 'Texas', 48);
    public static readonly UT = new StateCode('UT', 'Utah', 49);
    public static readonly VA = new StateCode('VA', 'Virginia', 51);
    public static readonly VT = new StateCode('VT', 'Vermont', 50);
    public static readonly WA = new StateCode('WA', 'Washington', 53);
    public static readonly WI = new StateCode('WI', 'Wisconsin', 55);
    public static readonly WV = new StateCode('WV', 'West Virginia', 54);
    public static readonly WY = new StateCode('WY', 'Wyoming', 56);

    // US Territories
    public static readonly AS = new StateCode('AS', 'American Samoa', 60, true);
    public static readonly FM = new StateCode('FM', 'Federated States of Micronesia', 64, true);
    public static readonly GU = new StateCode('GU', 'Guam', 66, true);
    public static readonly MH = new StateCode('MH', 'Marshall Islands', 68, true);
    public static readonly MP = new StateCode('MP', 'Northern Mariana Islands', 69, true);
    public static readonly PW = new StateCode('PW', 'Palau', 70, true);
    public static readonly PR = new StateCode('PR', 'Puerto Rico', 72, true);
    public static readonly VI = new StateCode('VI', 'Virgin Islands', 78, true);
    public static readonly AA = new StateCode('AA', 'Armed Forces America', 91, true);
    public static readonly AE = new StateCode('AE', 'Armed Forces Europe', 92, true);
    public static readonly AP = new StateCode('AP', 'Armed Forces Pacific', 93, true);
    public static readonly UM = new StateCode('UM', 'US Minor Outlying Islands', 74, true);

    public switch<T>({
        onAk,
        onAl,
        onAr,
        onAz,
        onCa,
        onCo,
        onCt,
        onDc,
        onDe,
        onFl,
        onGa,
        onHi,
        onIa,
        onId,
        onIl,
        onIn,
        onKs,
        onKy,
        onLa,
        onMa,
        onMd,
        onMe,
        onMi,
        onMn,
        onMo,
        onMs,
        onMt,
        onNc,
        onNd,
        onNe,
        onNh,
        onNj,
        onNm,
        onNv,
        onNy,
        onOh,
        onOk,
        onOr,
        onPa,
        onRi,
        onSc,
        onSd,
        onTn,
        onTx,
        onUt,
        onVa,
        onVt,
        onWa,
        onWi,
        onWv,
        onWy,
        onAs,
        onFm,
        onGu,
        onMh,
        onMp,
        onPw,
        onPr,
        onVi,
        onAa,
        onAe,
        onAp,
        onUm,
        onDefault,
    }: SwitchMethods<T>): T {
        switch (this.code) {
            case 'AK':
                return onAk ? onAk() : onDefault();
            case 'AL':
                return onAl ? onAl() : onDefault();
            case 'AR':
                return onAr ? onAr() : onDefault();
            case 'AZ':
                return onAz ? onAz() : onDefault();
            case 'CA':
                return onCa ? onCa() : onDefault();
            case 'CO':
                return onCo ? onCo() : onDefault();
            case 'CT':
                return onCt ? onCt() : onDefault();
            case 'DC':
                return onDc ? onDc() : onDefault();
            case 'DE':
                return onDe ? onDe() : onDefault();
            case 'FL':
                return onFl ? onFl() : onDefault();
            case 'GA':
                return onGa ? onGa() : onDefault();
            case 'HI':
                return onHi ? onHi() : onDefault();
            case 'IA':
                return onIa ? onIa() : onDefault();
            case 'ID':
                return onId ? onId() : onDefault();
            case 'IL':
                return onIl ? onIl() : onDefault();
            case 'IN':
                return onIn ? onIn() : onDefault();
            case 'KS':
                return onKs ? onKs() : onDefault();
            case 'KY':
                return onKy ? onKy() : onDefault();
            case 'LA':
                return onLa ? onLa() : onDefault();
            case 'MA':
                return onMa ? onMa() : onDefault();
            case 'MD':
                return onMd ? onMd() : onDefault();
            case 'ME':
                return onMe ? onMe() : onDefault();
            case 'MI':
                return onMi ? onMi() : onDefault();
            case 'MN':
                return onMn ? onMn() : onDefault();
            case 'MO':
                return onMo ? onMo() : onDefault();
            case 'MS':
                return onMs ? onMs() : onDefault();
            case 'MT':
                return onMt ? onMt() : onDefault();
            case 'NC':
                return onNc ? onNc() : onDefault();
            case 'ND':
                return onNd ? onNd() : onDefault();
            case 'NE':
                return onNe ? onNe() : onDefault();
            case 'NH':
                return onNh ? onNh() : onDefault();
            case 'NJ':
                return onNj ? onNj() : onDefault();
            case 'NM':
                return onNm ? onNm() : onDefault();
            case 'NV':
                return onNv ? onNv() : onDefault();
            case 'NY':
                return onNy ? onNy() : onDefault();
            case 'OH':
                return onOh ? onOh() : onDefault();
            case 'OK':
                return onOk ? onOk() : onDefault();
            case 'OR':
                return onOr ? onOr() : onDefault();
            case 'PA':
                return onPa ? onPa() : onDefault();
            case 'RI':
                return onRi ? onRi() : onDefault();
            case 'SC':
                return onSc ? onSc() : onDefault();
            case 'SD':
                return onSd ? onSd() : onDefault();
            case 'TN':
                return onTn ? onTn() : onDefault();
            case 'TX':
                return onTx ? onTx() : onDefault();
            case 'UT':
                return onUt ? onUt() : onDefault();
            case 'VA':
                return onVa ? onVa() : onDefault();
            case 'VT':
                return onVt ? onVt() : onDefault();
            case 'WA':
                return onWa ? onWa() : onDefault();
            case 'WI':
                return onWi ? onWi() : onDefault();
            case 'WV':
                return onWv ? onWv() : onDefault();
            case 'WY':
                return onWy ? onWy() : onDefault();
            case 'AS':
                return onAs ? onAs() : onDefault();
            case 'FM':
                return onFm ? onFm() : onDefault();
            case 'GU':
                return onGu ? onGu() : onDefault();
            case 'MH':
                return onMh ? onMh() : onDefault();
            case 'MP':
                return onMp ? onMp() : onDefault();
            case 'PW':
                return onPw ? onPw() : onDefault();
            case 'PR':
                return onPr ? onPr() : onDefault();
            case 'VI':
                return onVi ? onVi() : onDefault();
            case 'AA':
                return onAa ? onAa() : onDefault();
            case 'AE':
                return onAe ? onAe() : onDefault();
            case 'AP':
                return onAp ? onAp() : onDefault();
            case 'UM':
                return onUm ? onUm() : onDefault();
            default:
                return onDefault();
        }
    }
}

export namespace StateCode {
    export function all(includeTerritories: boolean = true): StateCode[] {
        const nonTerritories = [
            StateCode.AK,
            StateCode.AL,
            StateCode.AR,
            StateCode.AZ,
            StateCode.CA,
            StateCode.CO,
            StateCode.CT,
            StateCode.DC,
            StateCode.DE,
            StateCode.FL,
            StateCode.GA,
            StateCode.HI,
            StateCode.IA,
            StateCode.ID,
            StateCode.IL,
            StateCode.IN,
            StateCode.KS,
            StateCode.KY,
            StateCode.LA,
            StateCode.MA,
            StateCode.MD,
            StateCode.ME,
            StateCode.MI,
            StateCode.MN,
            StateCode.MO,
            StateCode.MS,
            StateCode.MT,
            StateCode.NC,
            StateCode.ND,
            StateCode.NE,
            StateCode.NH,
            StateCode.NJ,
            StateCode.NM,
            StateCode.NV,
            StateCode.NY,
            StateCode.OH,
            StateCode.OK,
            StateCode.OR,
            StateCode.PA,
            StateCode.RI,
            StateCode.SC,
            StateCode.SD,
            StateCode.TN,
            StateCode.TX,
            StateCode.UT,
            StateCode.VA,
            StateCode.VT,
            StateCode.WA,
            StateCode.WI,
            StateCode.WV,
            StateCode.WY,
        ];

        const territories = [
            StateCode.AS,
            StateCode.FM,
            StateCode.GU,
            StateCode.MH,
            StateCode.MP,
            StateCode.PW,
            StateCode.PR,
            StateCode.VI,
            StateCode.AA,
            StateCode.AE,
            StateCode.AP,
            StateCode.UM,
        ];

        return includeTerritories ? [...nonTerritories, ...territories] : nonTerritories;
    }

    export function parseOrDefault(value: string | number | null | undefined): StateCode | null {
        if (typeof value === 'number') {
            return StateCode.all().find(s => s.fips === value) ?? null;
        }

        if (typeof value === 'string') {
            return (
                StateCode.all().find(
                    s => s.code === value.toUpperCase() || s.name.toLowerCase() === value.toLowerCase()
                ) ?? null
            );
        }

        return null;
    }

    export function parse(value: string | number | null | undefined): StateCode {
        const state = parseOrDefault(value);
        if (state === null) {
            throw new Error(`Invalid StateCode: ${value}`);
        }

        return state;
    }
}

interface SwitchMethods<T> {
    onAk?: () => T;
    onAl?: () => T;
    onAr?: () => T;
    onAz?: () => T;
    onCa?: () => T;
    onCo?: () => T;
    onCt?: () => T;
    onDc?: () => T;
    onDe?: () => T;
    onFl?: () => T;
    onGa?: () => T;
    onHi?: () => T;
    onIa?: () => T;
    onId?: () => T;
    onIl?: () => T;
    onIn?: () => T;
    onKs?: () => T;
    onKy?: () => T;
    onLa?: () => T;
    onMa?: () => T;
    onMd?: () => T;
    onMe?: () => T;
    onMi?: () => T;
    onMn?: () => T;
    onMo?: () => T;
    onMs?: () => T;
    onMt?: () => T;
    onNc?: () => T;
    onNd?: () => T;
    onNe?: () => T;
    onNh?: () => T;
    onNj?: () => T;
    onNm?: () => T;
    onNv?: () => T;
    onNy?: () => T;
    onOh?: () => T;
    onOk?: () => T;
    onOr?: () => T;
    onPa?: () => T;
    onRi?: () => T;
    onSc?: () => T;
    onSd?: () => T;
    onTn?: () => T;
    onTx?: () => T;
    onUt?: () => T;
    onVa?: () => T;
    onVt?: () => T;
    onWa?: () => T;
    onWi?: () => T;
    onWv?: () => T;
    onWy?: () => T;
    onAs?: () => T;
    onFm?: () => T;
    onGu?: () => T;
    onMh?: () => T;
    onMp?: () => T;
    onPw?: () => T;
    onPr?: () => T;
    onVi?: () => T;
    onAa?: () => T;
    onAe?: () => T;
    onAp?: () => T;
    onUm?: () => T;
    onDefault: () => T;
}
